import { useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'
import * as api from '../api/client'
import { describeError } from '../api/errors'
import type { AudioTrack, ProjectMode, Render } from '../api/types'
import type { ProjectSocket } from '../api/ws'
import { pushToast } from '../lib/toast'
import { MODE_LABELS, trackTitle } from '../lib/tracks'

export type RenderPanelProps = {
  projectId: string
  socket: ProjectSocket | null
  ffmpegAvailable: boolean
  activeTakes: number
  mode: ProjectMode
  /** Дорожка-основа для режима replace; null — рендер не пойдёт. */
  meTrack: AudioTrack | null
}

const MUTE_DB = -99

export function RenderPanel({
  projectId,
  socket,
  ffmpegAvailable,
  activeTakes,
  mode,
  meTrack,
}: RenderPanelProps): JSX.Element {
  const [render, setRender] = useState<Render | null>(null)
  const [starting, setStarting] = useState(false)
  const [muteOriginal, setMuteOriginal] = useState(true)
  const [originalGain, setOriginalGain] = useState(-12)
  const [meGain, setMeGain] = useState(0)
  const renderRef = useRef<Render | null>(null)
  renderRef.current = render

  // WS — основной канал прогресса.
  useEffect(() => {
    if (!socket) return
    return socket.onEvent((event) => {
      const current = renderRef.current
      if (!current) return
      if (!('renderId' in event) || event.renderId !== current.id) return
      if (event.type === 'render.progress') {
        setRender({ ...current, status: 'running', progress: event.progress })
      } else if (event.type === 'render.done') {
        void api
          .getRender(current.id)
          .then(setRender)
          .catch(() => setRender({ ...current, status: 'done', progress: 1 }))
        pushToast('success', 'Рендер готов')
      } else if (event.type === 'render.error') {
        setRender({ ...current, status: 'error', error: event.error })
        pushToast('error', `Рендер не удался: ${event.error}`)
      }
    })
  }, [socket])

  // Поллинг — фолбэк, если WS недоступен.
  useEffect(() => {
    if (!render || (render.status !== 'pending' && render.status !== 'running')) return
    const id = render.id
    let cancelled = false
    const timer = window.setInterval(() => {
      void api
        .getRender(id)
        .then((next) => {
          if (cancelled) return
          setRender(next)
          if (next.status === 'done') pushToast('success', 'Рендер готов')
          if (next.status === 'error') {
            pushToast('error', `Рендер не удался: ${next.error ?? 'неизвестная ошибка'}`)
          }
        })
        .catch(() => undefined)
    }, 2000)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [render])

  const start = async (): Promise<void> => {
    setStarting(true)
    try {
      const created = await api.startRender(
        projectId,
        // В режиме mute поле mode не отправляем вообще: запрос обязан остаться
        // ровно таким же, каким его понимает старый бэкенд.
        mode === 'replace'
          ? { mode, meGain }
          : { originalGain: muteOriginal ? MUTE_DB : originalGain },
      )
      setRender(created)
      pushToast('info', 'Рендер поставлен в очередь')
    } catch (err) {
      pushToast('error', describeError(err))
    } finally {
      setStarting(false)
    }
  }

  const inProgress = render?.status === 'pending' || render?.status === 'running'
  const progressPercent = Math.round((render?.progress ?? 0) * 100)
  const replacing = mode === 'replace'
  const blockedByMe = replacing && !meTrack

  let disabledReason: string | null = null
  if (activeTakes === 0) disabledReason = 'Нет ни одного активного дубля'
  else if (!ffmpegAvailable) disabledReason = 'На сервере нет ffmpeg'
  else if (blockedByMe) disabledReason = 'В режиме замены нужна дорожка M&E'

  return (
    <section className="panel">
      <header>
        <h2>Рендер</h2>
        <span className="badge">{MODE_LABELS[mode]}</span>
        {!ffmpegAvailable ? (
          <span className="badge" style={{ color: 'var(--warning)' }}>
            ffmpeg не найден
          </span>
        ) : null}
      </header>

      <div className="stack">
        {replacing ? (
          <>
            {meTrack ? (
              <p className="faint" style={{ margin: 0 }}>
                Основа — «{trackTitle(meTrack)}». Звук из видеофайла и оригинальные реплики
                в микс не попадают.
              </p>
            ) : (
              <div className="notice warn">
                <strong>Дорожки M&E нет</strong>
                <span className="faint">
                  Сервер ответит «bad_request». Назначьте тип «M&E» одной из дорожек проекта или
                  загрузите её отдельным файлом в панели дорожек.
                </span>
              </div>
            )}
            <div>
              <label htmlFor="me-gain">
                Громкость M&E: <span className="mono">{meGain} дБ</span>
              </label>
              <input
                id="me-gain"
                type="range"
                min={-24}
                max={12}
                step={1}
                value={meGain}
                onChange={(e) => setMeGain(Number(e.currentTarget.value))}
              />
            </div>
          </>
        ) : (
          <>
            <label className="row" style={{ gap: 6, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={muteOriginal}
                onChange={(e) => setMuteOriginal(e.currentTarget.checked)}
              />
              <span style={{ color: 'var(--text)' }}>Заглушить оригинальную дорожку</span>
            </label>
            {!muteOriginal ? (
              <div>
                <label htmlFor="original-gain">
                  Громкость оригинала: <span className="mono">{originalGain} дБ</span>
                </label>
                <input
                  id="original-gain"
                  type="range"
                  min={-40}
                  max={0}
                  step={1}
                  value={originalGain}
                  onChange={(e) => setOriginalGain(Number(e.currentTarget.value))}
                />
              </div>
            ) : null}
          </>
        )}

        <div className="row wrap">
          <button
            type="button"
            className="primary"
            disabled={
              starting || inProgress || !ffmpegAvailable || activeTakes === 0 || blockedByMe
            }
            onClick={() => void start()}
            title={disabledReason ?? 'Собрать видео с новой дорожкой'}
          >
            {inProgress ? 'Рендерится…' : 'Собрать видео'}
          </button>
          <span className="faint">активных дублей: {activeTakes}</span>
        </div>

        {render ? (
          <div className="stack">
            <div className="progress">
              <i style={{ width: `${render.status === 'done' ? 100 : progressPercent}%` }} />
            </div>
            <div className="row">
              <span className="faint">
                {render.status === 'pending' ? 'В очереди' : null}
                {render.status === 'running' ? `Готово ${progressPercent}%` : null}
                {render.status === 'done' ? 'Готово' : null}
                {render.status === 'error' ? `Ошибка: ${render.error ?? 'неизвестно'}` : null}
              </span>
              <span className="spacer" />
              {render.status === 'done' ? (
                <a
                  className="badge"
                  href={api.renderUrl(render.id)}
                  download
                  style={{ color: 'var(--accent)' }}
                >
                  ⤓ Скачать MP4
                </a>
              ) : null}
            </div>
          </div>
        ) : null}

        {!ffmpegAvailable ? (
          <p className="faint">
            Сервер не нашёл ffmpeg в PATH. Разметка, запись и превью работают, финальную сборку
            включит установка ffmpeg.
          </p>
        ) : null}
      </div>
    </section>
  )
}
